// Ruta: packages/shared/src/constants/index.ts

// ─── RBAC Roles ──────────────────────────────────────────────────────────────

export const ROLES = {
  SUPERADMIN: 'superadmin',
  ADMIN: 'admin',
  ARRENDATARIO: 'arrendatario',
} as const

export type Role = (typeof ROLES)[keyof typeof ROLES]

// ─── Permissions ─────────────────────────────────────────────────────────────

export const PERMISSIONS = {
  STORES_READ: 'stores:read',
  DEVICES_READ: 'devices:read',
  MATERIALS_READ: 'materials:read',
  DRINKS_READ: 'drinks:read',
  SNACKS_READ: 'snacks:read',
  ORDERS_READ: 'orders:read',
  TROUBLES_READ: 'troubles:read',
  REPORTS_READ: 'reports:read',
  MONITORING_READ: 'monitoring:read',
  USERS_MANAGE: 'users:manage',
  ROLES_MANAGE: 'roles:manage',
} as const

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS]

// ─── Alert / Status color tokens ─────────────────────────────────────────────

export const STATUS_COLORS = {
  normal: 'green',
  warning: 'yellow',
  critical: 'red',
  offline: 'gray',
  unknown: 'gray',
} as const

// ─── Error code dictionary ────────────────────────────────────────────────────
// Source: SESION-003 — reverse-engineered from Lychee admin bundle JS (getName/getName2)
// and firmware co-occurrence pattern analysis.

export type ErrorSeverity = 'critical' | 'high' | 'warning' | 'medium' | 'low'
export type ErrorCategory = 'supply' | 'hardware' | 'payment' | 'dispensing' | 'firmware'

export interface ErrorCodeInfo {
  /** Numeric error code from the backend trouble module */
  code: number
  /** Human-readable description in Spanish */
  description: string
  /** Official English description from Lychee bundle (when available) */
  descriptionEn: string
  /** Operational severity level */
  severity: ErrorSeverity
  /** Functional category for grouping and filtering */
  category: ErrorCategory
  /** Operator action to resolve the error */
  actionRequired: string
  /**
   * Whether this mapping is confirmed from a Lychee bundle JS source.
   * false = hypothesis derived from firmware co-occurrence patterns (SESION-003 §4.2).
   * Unconfirmed codes are displayed with a visual indicator in the UI.
   */
  confirmed: boolean
}

/**
 * Complete dictionary of all known vending machine error codes.
 *
 * Confirmed codes (8): extracted verbatim from getName/getName2 function in
 *   app.038019a4e7c41284779b.js, chunk_2, and chunk_40 of the Lychee admin panel.
 *
 * Unconfirmed codes (4): 52, 2025, 2054, 2060 — not present in any of the 50
 *   Lychee JS bundles. Labeled as firmware-level events. Meaning inferred from
 *   temporal co-occurrence analysis. Pending confirmation from hardware vendor.
 */
export const ERROR_CODES: Readonly<Record<number, ErrorCodeInfo>> = {
  // ─── Confirmed codes (Lychee bundle source) ────────────────────────────────
  2062: {
    code: 2062,
    description: 'Sin vasos',
    descriptionEn: 'Empty Cup',
    severity: 'critical',
    category: 'supply',
    actionRequired: 'Reponer vasos en la máquina inmediatamente',
    confirmed: true,
  },
  2059: {
    code: 2059,
    description: 'Fallo en bomba de agua caliente',
    descriptionEn: 'Water Pumping Malfunction',
    severity: 'critical',
    category: 'hardware',
    actionRequired: 'Revisar bomba de agua caliente — posible fallo mecánico. Requiere técnico.',
    confirmed: true,
  },
  2049: {
    code: 2049,
    description: 'Preparación de bebida fallida',
    descriptionEn: 'Beverage Preparation Failed',
    severity: 'high',
    category: 'dispensing',
    actionRequired: 'Verificar ingredientes y sistema de dispensación. Reiniciar si persiste.',
    confirmed: true,
  },
  1008: {
    code: 1008,
    description: 'Fallo en dispensación de producto',
    descriptionEn: 'Product Dispensing Failed',
    severity: 'high',
    category: 'dispensing',
    actionRequired: 'Verificar mecanismo de dispensación y stock de snacks',
    confirmed: true,
  },
  2005: {
    code: 2005,
    description: 'Preparación de bebida fallida',
    descriptionEn: 'Beverage Preparation Failed',
    severity: 'high',
    category: 'dispensing',
    actionRequired: 'Verificar ingredientes, agua y mecanismo de mezcla',
    confirmed: true,
  },
  2021: {
    code: 2021,
    description: 'Reembolso fallido',
    descriptionEn: 'Refund Failed',
    severity: 'medium',
    category: 'payment',
    actionRequired: 'Procesar reembolso manual al cliente. Revisar módulo de pagos.',
    confirmed: true,
  },
  2024: {
    code: 2024,
    description: 'Lector de tarjetas offline',
    descriptionEn: 'Card Reader Offline',
    severity: 'medium',
    category: 'payment',
    actionRequired: 'Verificar conexión del lector de tarjetas. Reiniciar periférico.',
    confirmed: true,
  },
  2044: {
    code: 2044,
    description: 'Caja registradora offline',
    descriptionEn: 'Cash Register Offline',
    severity: 'medium',
    category: 'payment',
    actionRequired: 'Verificar conexión del módulo de efectivo. Reiniciar periférico.',
    confirmed: true,
  },

  // ─── Unconfirmed codes (firmware-level — not in Lychee JS bundles) ─────────
  // Source: temporal co-occurrence pattern analysis (SESION-003 §4.2)
  52: {
    code: 52,
    description: 'Pulso de firmware (sin confirmar)',
    descriptionEn: 'Firmware pulse (unconfirmed)',
    severity: 'low',
    category: 'firmware',
    actionRequired: 'Sin acción requerida — posible evento de activación de motor. Monitorear.',
    confirmed: false,
  },
  2054: {
    code: 2054,
    description: 'Error de temperatura (sin confirmar)',
    descriptionEn: 'Temperature error (unconfirmed)',
    severity: 'warning',
    category: 'hardware',
    actionRequired:
      'Verificar temperatura del tanque de agua caliente. Co-ocurre con fallo de bomba (2059).',
    confirmed: false,
  },
  2025: {
    code: 2025,
    description: 'Nivel bajo de agua (sin confirmar)',
    descriptionEn: 'Low water level (unconfirmed)',
    severity: 'warning',
    category: 'supply',
    actionRequired: 'Verificar nivel del tanque de agua. Reabastecer si está bajo.',
    confirmed: false,
  },
  2060: {
    code: 2060,
    description: 'Nivel crítico de vasos (sin confirmar)',
    descriptionEn: 'Critical cup level (unconfirmed)',
    severity: 'warning',
    category: 'supply',
    actionRequired: 'Reponer vasos pronto — precede a alerta "Sin vasos" (2062).',
    confirmed: false,
  },
} as const

/**
 * Retrieves the full ErrorCodeInfo for a given error code string or number.
 * Returns undefined for unknown codes — callers should handle the unknown case explicitly.
 */
export function getErrorCodeInfo(code: string | number): ErrorCodeInfo | undefined {
  return ERROR_CODES[Number(code)]
}

/**
 * Returns the Spanish description for an error code.
 * Falls back to `Código {code}` for unknown codes.
 * Preserved for backward compatibility with existing callers.
 */
export function getAlertLabel(code: string): string {
  return ERROR_CODES[Number(code)]?.description ?? `Código ${code}`
}

/** Ordered list of all severity levels, from most to least severe. */
export const SEVERITY_ORDER: ReadonlyArray<ErrorSeverity> = [
  'critical',
  'high',
  'warning',
  'medium',
  'low',
]

/** Human-readable Spanish labels for each severity level */
export const SEVERITY_LABELS: Readonly<Record<ErrorSeverity, string>> = {
  critical: 'Crítico',
  high: 'Alto',
  warning: 'Advertencia',
  medium: 'Medio',
  low: 'Bajo',
}

/** Human-readable Spanish labels for each error category */
export const CATEGORY_LABELS: Readonly<Record<ErrorCategory, string>> = {
  supply: 'Insumos',
  hardware: 'Hardware',
  payment: 'Pagos',
  dispensing: 'Dispensación',
  firmware: 'Firmware',
}

// ─── Polling intervals (ms) ───────────────────────────────────────────────────

export const POLL_INTERVALS = {
  ALERTS: 30_000,
  MATERIALS: 120_000,
  DASHBOARD: 300_000,
  MONITORING: 60_000,
  HEALTH: 30_000,
} as const

// ─── Pagination defaults ──────────────────────────────────────────────────────

export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_PAGE_SIZE: 20,
  ALERT_HISTORY_PAGE_SIZE: 50,
} as const

// ─── Routes (for navigation) ─────────────────────────────────────────────────

export const DASHBOARD_ROUTES = {
  LOGIN: '/login',
  OVERVIEW: '/overview',
  STORE: (storeId: number) => `/stores/${storeId}`,
  STORE_DEVICES: (storeId: number) => `/stores/${storeId}/devices`,
  STORE_DRINKS: (storeId: number) => `/stores/${storeId}/drinks`,
  STORE_DRINK_DETAIL: (storeId: number, drinkId: number) => `/stores/${storeId}/drinks/${drinkId}`,
  STORE_SNACKS: (storeId: number) => `/stores/${storeId}/snacks`,
  STORE_SNACK_DETAIL: (storeId: number, snackId: number) => `/stores/${storeId}/snacks/${snackId}`,
  STORE_ORDERS: (storeId: number) => `/stores/${storeId}/orders`,
  STORE_ORDER_DETAIL: (storeId: number, orderId: number) => `/stores/${storeId}/orders/${orderId}`,
  STORE_ALERTS: (storeId: number) => `/stores/${storeId}/alerts`,
  STORE_PROFIT: (storeId: number) => `/stores/${storeId}/profit`,
  STORE_WEEKLY: (storeId: number) => `/stores/${storeId}/weekly`,
  STORE_PAYMENT_METHODS: (storeId: number) => `/stores/${storeId}/payment-methods`,
  DEVICE_DETAIL: (storeId: number, deviceId: number) => `/stores/${storeId}/devices/${deviceId}`,
  DEVICE_INVENTORY: (storeId: number, deviceId: number) =>
    `/stores/${storeId}/devices/${deviceId}/inventory`,
  DEVICE_INVENTORY_TREND: (storeId: number, deviceId: number) =>
    `/stores/${storeId}/devices/${deviceId}/inventory/trend`,
  MONITORING: '/monitoring',
  UNAUTHORIZED: '/unauthorized',
} as const

export const CMS_ROUTES = {
  LOGIN: '/login',
  USERS: '/users',
  USERS_CREATE: '/users/create',
  USER_EDIT: (userId: number) => `/users/${userId}`,
  ROLES: '/roles',
  STORES: '/stores',
  STORES_REGISTER: '/stores/register',
  SETTINGS: '/settings',
  AUDIT: '/audit',
  UNAUTHORIZED: '/unauthorized',
} as const
